import React from "react"
import { FaInstagram, FaTwitter, FaTiktok, FaApple } from "react-icons/fa"
import { Analytics } from "@vercel/analytics/react"
import { useTranslation } from "react-i18next"
import GradientBackground from './sections/GradientBackground'
import { Menu, CheckCircle2, Star } from "lucide-react"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import PartnersSection from './sections/PartnersSection'
import SEO from './common/SEO'
import LanguageSwitcher from './common/LanguageSwitcher'
import { localizedPath } from '@/lib/localizedPath'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ children, className = "", ...props }) => {
  return (
    <button
      className={`inline-flex items-center justify-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

interface FeatureCardProps {
  mockupImage: string;
  title: string;
  description: string;
}

interface SocialIconProps {
  Icon: React.ComponentType<{ className: string }>;
  href: string;
  label: string;
}

export const Header: React.FC = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  return (
    <header className="fixed top-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-auto rounded-full bg-white/70 backdrop-blur-xl border border-white/60 z-50 shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
      <div className="flex h-16 items-center justify-between sm:justify-start gap-8 px-4 sm:px-6">
        <a href={localizedPath('/', lang)} className="flex items-center space-x-2 shrink-0">
          <img src="/currents-logo.png" alt="Currents Logo" className="w-8 h-8 rounded-lg" />
          <span className="hidden sm:inline text-lg font-black tracking-tight">Currents</span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden items-center space-x-8 text-sm font-medium md:flex">
          <a href={localizedPath('/', lang)} className="text-foreground/60 transition hover:text-foreground">
            {t('nav.home')}
          </a>
          <a href={localizedPath('/about', lang)} className="text-foreground/60 transition hover:text-foreground">
            {t('nav.about')}
          </a>
          <a href={localizedPath('/blog', lang)} className="text-foreground/60 transition hover:text-foreground">
            {t('nav.blog')}
          </a>
          <LanguageSwitcher />
        </nav>

        {/* Mobile Navigation */}
        <div className="flex items-center md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button className="p-2 hover:bg-transparent rounded-full">
                <Menu className="h-6 w-6" />
                <span className="sr-only">{t('nav.toggleMenu')}</span>
              </button>
            </SheetTrigger>
            <SheetContent side="top" className="h-screen pt-16">
              <nav className="flex flex-col space-y-8 items-center text-center">
                <a href={localizedPath('/', lang)} className="text-2xl font-medium hover:text-[#1F80FF]">
                  {t('nav.home')}
                </a>
                <a href={localizedPath('/about', lang)} className="text-2xl font-medium hover:text-[#1F80FF]">
                  {t('nav.about')}
                </a>
                <a href={localizedPath('/blog', lang)} className="text-2xl font-medium hover:text-[#1F80FF]">
                  {t('nav.blog')}
                </a>
                <div className="h-px w-24 bg-gray-200" />
                <a href={localizedPath('/terms-of-service', lang)} className="text-xl text-muted-foreground hover:text-[#1F80FF]">
                  {t('nav.legal.terms')}
                </a>
                <a href={localizedPath('/privacy-policy', lang)} className="text-xl text-muted-foreground hover:text-[#1F80FF]">
                  {t('nav.legal.privacy')}
                </a>
                <LanguageSwitcher className="text-xl" />
                <div className="w-full max-w-[300px] pt-4">
                  <Button
                    className="rounded-full bg-zinc-900 text-white px-8 py-3 w-full gap-2 hover:bg-zinc-800"
                    onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
                  >
                    <FaApple className="w-4 h-4" /> {t('nav.downloadApp')}
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop Download Button */}
        <div className="hidden md:block">
          <Button
            className="rounded-full bg-zinc-900 text-white px-5 py-2.5 gap-2 hover:bg-zinc-800"
            onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
          >
            <FaApple className="w-4 h-4" /> {t('nav.downloadAppShort')}
          </Button>
        </div>
      </div>
    </header>
  )
}

const HeroSection: React.FC = () => {
  const { t } = useTranslation()
  return (
    <section className="container flex flex-col items-center justify-center space-y-5 pt-40 md:pt-48 px-4 text-center">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white px-4 py-1.5 text-xs font-semibold text-[#1F80FF] shadow-sm">
        <FaApple className="w-3.5 h-3.5" /> {t('home.hero.badge')}
      </div>
      <h1 className="max-w-4xl text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
        {t('home.hero.title')}
      </h1>
      <p className="max-w-[600px] text-lg md:text-xl text-muted-foreground">
        {t('home.hero.body')}
      </p>
      <Button
        className="mt-2 rounded-full bg-zinc-900 px-8 py-3.5 text-white hover:bg-zinc-800 gap-2 text-base"
        onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
      >
        <FaApple className="w-4 h-4" /> {t('home.hero.cta')}
      </Button>
    </section>
  )
}

/* Ported from Currents-Mock/statistics.jsx (TrendCard + Sparkline/SparklineThreshold/DotRow) */
const trendPalette = {
  ink: '#14213D',
  inkSoft: 'rgba(20,33,61,0.55)',
  inkMute: 'rgba(20,33,61,0.34)',
  inkLine: 'rgba(20,33,61,0.06)',
  warn: '#D89B7E',
}

const Sparkline: React.FC<{ series: number[]; avg?: number; color: string }> = ({ series, avg, color }) => {
  const id = React.useId()
  const W = 130, H = 34, padX = 2, padY = 4
  const innerW = W - padX * 2, innerH = H - padY * 2
  const max = Math.max(...series, avg || 0)
  const min = Math.min(...series, avg ?? Infinity)
  const range = (max - min) || 1
  const xAt = (i: number) => padX + (i / (series.length - 1)) * innerW
  const yAt = (v: number) => padY + innerH - ((v - min) / range) * innerH
  const pts = series.map((v, i) => [xAt(i), yAt(v)])
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L ${pts[pts.length - 1][0].toFixed(1)} ${(padY + innerH).toFixed(1)} L ${pts[0][0].toFixed(1)} ${(padY + innerH).toFixed(1)} Z`
  const [endX, endY] = pts[pts.length - 1]
  const avgY = avg != null ? yAt(avg) : null
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {avgY != null && (
        <line x1={padX} y1={avgY} x2={padX + innerW} y2={avgY} stroke={trendPalette.inkMute} strokeWidth="1" strokeDasharray="2 3" opacity="0.7" />
      )}
      <path d={areaPath} fill={`url(#spark-${id})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={endX} cy={endY} r="3" fill={color} stroke="#fff" strokeWidth="1.4" />
    </svg>
  )
}

const SparklineThreshold: React.FC<{ series: number[]; threshold: number; thresholdLabel: string; color: string }> = ({ series, threshold, thresholdLabel, color }) => {
  const id = React.useId()
  const W = 130, H = 34, padX = 2, padY = 4
  const innerW = W - padX * 2, innerH = H - padY * 2
  const max = Math.max(...series, threshold)
  const min = Math.min(...series)
  const range = (max - min) || 1
  const xAt = (i: number) => padX + (i / (series.length - 1)) * innerW
  const yAt = (v: number) => padY + innerH - ((v - min) / range) * innerH
  const pts = series.map((v, i) => [xAt(i), yAt(v)])
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L ${pts[pts.length - 1][0].toFixed(1)} ${(padY + innerH).toFixed(1)} L ${pts[0][0].toFixed(1)} ${(padY + innerH).toFixed(1)} Z`
  const [endX, endY] = pts[pts.length - 1]
  const thY = yAt(threshold)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={`sparkT-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1={padX} y1={thY} x2={padX + innerW} y2={thY} stroke={trendPalette.warn} strokeWidth="1" strokeDasharray="3 3" opacity="0.85" />
      <text x={padX + innerW - 2} y={thY - 3} textAnchor="end" fontSize="7" fontWeight={700} fill={trendPalette.warn} opacity="0.95">{thresholdLabel}</text>
      <path d={areaPath} fill={`url(#sparkT-${id})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={endX} cy={endY} r="3" fill={color} stroke="#fff" strokeWidth="1.4" />
    </svg>
  )
}

const DotRow: React.FC<{ series: number[]; color: string }> = ({ series, color }) => {
  const W = 130, H = 34, r = 3.2
  const gap = (W - r * 2) / (series.length - 1)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
      {series.map((v, i) => {
        const cx = r + i * gap, cy = H / 2
        return v === 1
          ? <circle key={i} cx={cx} cy={cy} r={r} fill={color} />
          : <circle key={i} cx={cx} cy={cy} r={r - 0.4} fill="none" stroke={trendPalette.inkLine} strokeWidth="1.2" />
      })}
    </svg>
  )
}

interface TrendCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  unit?: string;
  status: string;
  accent: string;
  viz: React.ReactNode;
  className: string;
  animate: string;
  mobileHidden?: boolean;
}

const TrendCard: React.FC<TrendCardProps> = ({ icon, label, value, unit, status, accent, viz, className, animate, mobileHidden }) => (
  <div className={`glass-card ${animate} absolute z-30 ${mobileHidden ? 'hidden sm:block' : 'block'} w-[164px] rounded-[18px] px-3.5 py-3 ${className}`}>
    <div className="flex items-center gap-1.5 mb-2" style={{ color: trendPalette.inkSoft }}>
      {icon}
      <span className="text-[11px] font-bold" style={{ color: trendPalette.ink }}>{label}</span>
    </div>
    <div className="flex items-baseline gap-1 mb-1.5">
      <span className="text-[22px] font-extrabold leading-none tabular-nums" style={{ color: trendPalette.ink, letterSpacing: '-0.02em' }}>{value}</span>
      {unit && <span className="text-[10px] font-semibold" style={{ color: trendPalette.inkSoft }}>{unit}</span>}
    </div>
    <div className="mb-1.5">{viz}</div>
    <div className="flex items-center gap-1 text-[11px] font-bold" style={{ color: accent }}>
      <CheckCircle2 className="w-3 h-3 shrink-0" />
      <span className="truncate">{status}</span>
    </div>
  </div>
)

const AppShowcase: React.FC = () => {
  const { t } = useTranslation()
  return (
    <section className="container pt-8 pb-16 px-4">
      <div className="relative flex justify-center items-end h-[380px] sm:h-[520px] md:h-[620px]">
        {/* Floating glass stat cards */}
        <TrendCard
          className="left-[1%] sm:left-[4%] top-[-25px] sm:top-[2%] -rotate-3"
          animate="animate-float-1"
          accent="#5C9C7F"
          icon={
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="13" r="7" /><path d="M12 13l3-3M12 4v2" />
            </svg>
          }
          label={t('home.showcase.sacRate.label')}
          value="18"
          unit={t('home.showcase.sacRate.unit')}
          viz={<Sparkline series={[22, 21, 22, 20, 21, 19, 20, 19, 18, 19, 18, 17, 18, 17, 18, 17, 16, 17, 18, 17, 16, 17, 16, 17, 16, 18, 17, 16, 17, 18]} avg={19} color="#5C9C7F" />}
          status={t('home.showcase.sacRate.status')}
        />

        <TrendCard
          className="right-[0%] sm:right-[2%] top-[56%] sm:top-[16%] rotate-3"
          animate="animate-float-2"
          accent="#5C9C7F"
          icon={
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          }
          label={t('home.showcase.safetyStop.label')}
          value="94"
          unit={t('home.showcase.safetyStop.unit')}
          viz={<DotRow series={[1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]} color="#5C9C7F" />}
          status={t('home.showcase.safetyStop.status')}
        />

        <TrendCard
          mobileHidden
          className="left-[0%] sm:left-[1%] bottom-[8%] rotate-2"
          animate="animate-float-3"
          accent="#5C9C7F"
          icon={
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 2h6v4l3 3v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9l3-3z" />
            </svg>
          }
          label={t('home.showcase.airManagement.label')}
          value="62"
          unit={t('home.showcase.airManagement.unit')}
          viz={<SparklineThreshold series={[45, 50, 55, 48, 60, 58, 65, 55, 70, 62, 58, 72, 60, 68, 55, 65, 70, 60, 58, 62]} threshold={50} thresholdLabel={t('home.showcase.airManagement.thresholdLabel')} color="#D89B7E" />}
          status={t('home.showcase.airManagement.status')}
        />

        <TrendCard
          mobileHidden
          className="right-[2%] sm:right-[10%] bottom-[2%] -rotate-2"
          animate="animate-float-4"
          accent="#5C9C7F"
          icon={
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          }
          label={t('home.showcase.ascentRate.label')}
          value="8.2"
          unit={t('home.showcase.ascentRate.unit')}
          viz={<SparklineThreshold series={[9.1, 8.9, 8.6, 8.8, 8.7, 8.5, 8.4, 8.3, 8.5, 8.2, 8.4, 8.3, 8.2, 8.1, 8.3, 8.0, 7.9, 8.2, 8.1, 8.0, 8.2, 8.1, 7.8, 8.0, 7.9, 8.1, 8.2, 7.9, 8.1, 8.2]} threshold={9} thresholdLabel={t('home.showcase.ascentRate.thresholdLabel')} color="#2E6FD9" />}
          status={t('home.showcase.ascentRate.status')}
        />

        {/* Left phone: dive site */}
        <img
          src="/currents-app-dive-site.png"
          alt=""
          className="hidden sm:block absolute left-1/2 -translate-x-[195%] bottom-2 sm:bottom-6 h-[280px] sm:h-[380px] md:h-[440px] w-auto object-contain drop-shadow-2xl -rotate-[14deg] opacity-90 z-0"
        />
        {/* Right phone: dive computer sync */}
        <img
          src="/currents-app-sync-computer.png"
          alt=""
          className="hidden sm:block absolute left-1/2 translate-x-[95%] bottom-2 sm:bottom-6 h-[280px] sm:h-[380px] md:h-[440px] w-auto object-contain drop-shadow-2xl rotate-[14deg] opacity-90 z-0"
        />
        {/* Center phone: collaborative dive log */}
        <img
          src="/currents-app-collab-dive.png"
          alt="Currents App Mockup"
          className="relative z-10 h-[380px] sm:h-[540px] md:h-[620px] w-auto object-contain drop-shadow-2xl rotate-[3deg]"
        />
      </div>
    </section>
  )
}

const CompactFeatureCard: React.FC<FeatureCardProps & { tint: string; reverse?: boolean }> = ({ mockupImage, title, description, tint, reverse }) => {
  const textBlock = (
    <div className={`p-6 sm:p-8 relative z-10 ${reverse ? 'pt-0' : 'pb-0'}`}>
      <h3 className="text-xl sm:text-2xl font-black tracking-tight leading-tight mb-2">{title}</h3>
      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
        {description}
      </p>
    </div>
  )
  const imageBlock = (
    <div className={`relative flex-1 min-h-[260px] sm:min-h-[320px] ${reverse ? '-mb-2' : '-mt-2'}`}>
      <img
        src={mockupImage}
        alt={`${title} Feature`}
        className={`absolute left-1/2 -translate-x-1/2 h-[500px] sm:h-[600px] w-auto max-w-none object-contain drop-shadow-2xl ${reverse ? '-bottom-4 sm:-bottom-6' : '-top-4 sm:-top-6'}`}
      />
    </div>
  )
  return (
    <div className={`rounded-[2rem] ${tint} overflow-hidden flex flex-col h-full`}>
      {reverse ? (
        <>
          {imageBlock}
          {textBlock}
        </>
      ) : (
        <>
          {textBlock}
          {imageBlock}
        </>
      )}
    </div>
  )
}

interface StickerProps {
  src: string;
  className: string;
  front?: boolean;
}

const FeatureCard: React.FC<FeatureCardProps & { tint: string; stickers?: StickerProps[] }> = ({ mockupImage, title, description, tint, stickers }) => {
  return (
    <div className={`rounded-[2rem] ${tint} overflow-hidden`}>
      <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-4 px-6 sm:px-10 lg:px-16 py-10 lg:py-14">
        <div className="flex-1 text-center lg:text-left">
          <h3 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-4">{title}</h3>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md mx-auto lg:mx-0">
            {description}
          </p>
        </div>
        <div className="relative flex-1 flex items-center justify-center w-full h-[320px] sm:h-[420px] lg:h-[480px]">
          {stickers?.filter(s => !s.front).map((sticker, i) => (
            <img key={i} src={sticker.src} alt="" className={`absolute z-0 object-contain sticker-cutout pointer-events-none ${sticker.className}`} />
          ))}
          <img
            src={mockupImage}
            alt={`${title} Feature`}
            className="relative z-10 h-[420px] sm:h-[560px] lg:h-[640px] w-auto object-contain drop-shadow-xl"
          />
          {stickers?.filter(s => s.front).map((sticker, i) => (
            <img key={i} src={sticker.src} alt="" className={`absolute z-20 object-contain sticker-cutout pointer-events-none ${sticker.className}`} />
          ))}
        </div>
      </div>
    </div>
  )
}

const FeaturesSection: React.FC = () => {
  const { t } = useTranslation()
  const features: FeatureCardProps[] = [
    {
      mockupImage: "/currents-app-logbook-screen.png",
      title: t('home.features.items.logbook.title'),
      description: t('home.features.items.logbook.description')
    },
    {
      mockupImage: "/currents-app-collab-dive.png",
      title: t('home.features.items.social.title'),
      description: t('home.features.items.social.description')
    },
    {
      mockupImage: "/currents-app-dive-site.png",
      title: t('home.features.items.planning.title'),
      description: t('home.features.items.planning.description')
    }
  ]

  const moreFeatures: FeatureCardProps[] = [
    {
      mockupImage: "/currents-app-widgets.png",
      title: t('home.features.items.widgets.title'),
      description: t('home.features.items.widgets.description')
    },
    {
      mockupImage: "/currents-app-dive-atlas.png",
      title: t('home.features.items.diveAtlas.title'),
      description: t('home.features.items.diveAtlas.description')
    }
  ]

  const bentoFeature: FeatureCardProps = {
    mockupImage: "/currents-app-gear-locker.png",
    title: t('home.features.items.gear.title'),
    description: t('home.features.items.gear.description')
  }

  const tints = ['panel-tint-blue', 'panel-tint-lavender', 'panel-tint-mint']

  return (
    <section className="container max-w-[1400px] pt-8 pb-20 px-4">
      <div className="text-center">
        <div className="inline-block rounded-full bg-[#1F80FF]/10 px-3 py-1 text-sm font-semibold text-[#1F80FF]">
          {t('home.features.badge')}
        </div>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">
          {t('home.features.title')}
        </h2>
        <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
          {t('home.features.subtitle')}
        </p>
      </div>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <CompactFeatureCard key={index} {...feature} tint={tints[index % tints.length]} />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {moreFeatures.map((feature, index) => (
          <CompactFeatureCard key={index} {...feature} tint={tints[(index + 1) % tints.length]} reverse={index === 1} />
        ))}
      </div>
      <div className="mt-6">
        <FeatureCard
          {...bentoFeature}
          tint="panel-tint-blue"
          stickers={[
            { src: "/currents-gear-sticker-computer.png", className: "h-24 sm:h-32 left-[4%] top-[6%] -rotate-6" },
            { src: "/currents-gear-sticker-camera.png", className: "h-14 sm:h-16 right-[6%] top-[-6%] rotate-12" },
            { src: "/currents-gear-sticker-fins.png", className: "h-24 sm:h-28 right-[0%] bottom-[0%] rotate-[10deg]" },
            { src: "/currents-gear-sticker-mask.png", className: "h-16 sm:h-24 left-[3%] bottom-[8%] -rotate-6", front: true },
            { src: "/currents-gear-sticker-wetsuit.png", className: "h-48 sm:h-64 right-[2%] top-[46%] -translate-y-1/2 rotate-6", front: true },
          ]}
        />
      </div>
    </section>
  )
}

const EssaySection: React.FC = () => {
  const { t } = useTranslation()
  const sections = [
    { key: 'story', body: [t('home.essay.story.body')] },
    { key: 'whatItDoes', body: [t('home.essay.whatItDoes.body1'), t('home.essay.whatItDoes.body2')] },
    { key: 'whoFor', body: [t('home.essay.whoFor.body')] },
    { key: 'limitations', body: [t('home.essay.limitations.body')] },
    { key: 'competitors', body: [t('home.essay.competitors.body')] },
  ]

  return (
    <section className="container py-8 px-4">
      <div className="panel-paper rotate-0 sm:-rotate-1 shadow-xl rounded-[2rem] sm:rounded-[2.5rem] max-w-[760px] mx-auto px-5 sm:px-12 lg:px-16 pt-24 pb-14 sm:py-20">
        <div className="absolute -top-6 right-3 sm:-top-12 sm:right-8 rotate-6 z-10 bg-white p-2 pb-5 sm:pb-6 shadow-lg w-24 sm:w-40">
          <img src="/latisha-profile.jpg" alt="Latisha diving" className="w-full aspect-square object-cover block" />
        </div>
        <div className="font-handwritten relative z-[1]">
          <p className="text-base text-muted-foreground mb-10 pr-24 sm:pr-0">{t('home.essay.byline')}</p>
          <div className="space-y-12">
            {sections.map((section) => (
              <div key={section.key}>
                <h2 className="text-3xl tracking-tight mb-3">
                  {t(`home.essay.${section.key}.heading`)}
                </h2>
                {section.body.map((paragraph, i) => (
                  <p key={i} className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}
          </div>
          <p className="mt-12 text-xl">{t('home.essay.closing')}</p>
        </div>
      </div>
    </section>
  )
}

interface Review {
  stars: number;
  title: string;
  name: string;
  location: string;
  date: string;
  quote: string;
}

// Real App Store reviews only. Extend this array as more come in.
const REVIEWS: Review[] = [
  {
    stars: 5,
    title: "Great app",
    name: "BroGim99",
    location: "Indonesia",
    date: "September 7, 2024",
    quote: "Thank u for creating app like this, can't wait to see more update",
  },
  {
    stars: 5,
    title: "Beautiful and thoughtfully designed",
    name: "BrooklynKid99",
    location: "United States",
    date: "July 26, 2026",
    quote: "Really impressed with this app. It makes logging dives quick and easy, and I like that it supports importing data from dive computers. The dive site information and clean interface make planning and tracking dives much more enjoyable. A great tool for any scuba diver.",
  },
  {
    stars: 5,
    title: "Best diving app!",
    name: "GretskyOne",
    location: "United States",
    date: "August 29, 2026",
    quote: "Great sync with my computer. Love that you can merge multiple dives of the same day, and even geo locate the dive with the photos taken. Well done!",
  },
]

const ReviewCard: React.FC<{ review: Review; tint: string }> = ({ review, tint }) => (
  <div className={`shrink-0 w-[300px] sm:w-[340px] rounded-[2rem] ${tint} p-6 sm:p-7`}>
    <div className="flex gap-1 mb-3" aria-label={`${review.stars} out of 5 stars`}>
      {Array.from({ length: review.stars }).map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-[#F5A623] text-[#F5A623]" />
      ))}
    </div>
    <h3 className="text-lg font-bold tracking-tight mb-1">{review.title}</h3>
    <p className="text-xs text-muted-foreground mb-4">{review.name} &middot; {review.location} &middot; {review.date}</p>
    <p className="text-sm text-foreground/80 leading-relaxed">{review.quote}</p>
  </div>
)

const ReviewsSection: React.FC = () => {
  const { t } = useTranslation()
  // Repeat the real reviews to fill a wide track, then duplicate the whole
  // sequence once more so the -50% translateX loop is seamless.
  const sequence = Array.from({ length: 4 }).flatMap(() => REVIEWS)
  const track = [...sequence, ...sequence]

  return (
    <section className="pt-8 pb-16">
      <div className="container text-center px-4">
        <div className="inline-block rounded-full bg-[#1F80FF]/10 px-3 py-1 text-sm font-semibold text-[#1F80FF]">
          {t('home.reviews.badge')}
        </div>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">
          {t('home.reviews.title')}
        </h2>
        <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
          {t('home.reviews.subtitle')}
        </p>
      </div>
      <div className="mt-12 overflow-hidden" style={{ maskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)' }}>
        <div className="flex w-max gap-6 marquee-track px-4">
          {track.map((review, i) => (
            <ReviewCard key={i} review={review} tint="panel-tint-blue" />
          ))}
        </div>
      </div>
    </section>
  )
}

const CtaSection: React.FC = () => {
  const { t } = useTranslation()
  const items = [
    // wetsuit: big, laid diagonally on the left
    { src: "/currents-gear-sticker-wetsuit.png", z: 10, className: "h-[200px] sm:h-[300px] md:h-[380px] left-[0%] sm:left-[6%] bottom-0 -rotate-[12deg]" },
    // fins: big, laid diagonally on the right
    { src: "/currents-gear-sticker-fins.png", z: 10, className: "h-[180px] sm:h-[260px] md:h-[320px] right-[0%] sm:right-[6%] bottom-0 rotate-[14deg]" },
    // phone: the focal point, front and center
    { src: "/currents-app-logbook-screen.png", z: 20, plain: true, className: "h-[220px] sm:h-[320px] md:h-[400px] left-1/2 -translate-x-1/2 bottom-0 -rotate-2" },
    // mask: small, resting near top-left, in front
    { src: "/currents-gear-sticker-mask.png", z: 30, className: "h-[64px] sm:h-[100px] md:h-[130px] left-[18%] sm:left-[24%] bottom-[70%] sm:bottom-[65%] -rotate-[10deg]" },
    // dive computer: tucked top-right, in front
    { src: "/currents-gear-sticker-computer.png", z: 30, className: "h-[80px] sm:h-[124px] md:h-[160px] right-[18%] sm:right-[24%] bottom-[64%] sm:bottom-[58%] rotate-[12deg]" },
    // camera: tucked between the phone and fins, in front
    { src: "/currents-gear-sticker-camera.png", z: 30, className: "h-[64px] sm:h-[96px] md:h-[120px] right-[14%] sm:right-[18%] bottom-[6%] rotate-[18deg]" },
  ]

  return (
    <section className="relative pt-16 md:pt-20 pb-8 overflow-x-clip">
      <div className="container relative z-40 flex flex-col items-center space-y-5 text-center px-4">
        <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[0.95]">{t('home.cta.title')}</h2>
        <p className="max-w-[520px] text-lg text-muted-foreground">
          {t('home.cta.body')}
        </p>
        <Button
          className="mt-2 rounded-full bg-zinc-900 px-8 py-3.5 text-white hover:bg-zinc-800 gap-2"
          onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
        >
          <FaApple className="w-4 h-4" /> {t('home.cta.button')}
        </Button>
      </div>
      <div className="relative h-[220px] sm:h-[320px] md:h-[400px] mt-10 w-full">
        {items.map((item, i) => (
          <img
            key={i}
            src={item.src}
            alt=""
            style={{ zIndex: item.z }}
            className={`absolute w-auto object-contain ${item.plain ? 'drop-shadow-xl' : 'sticker-cutout'} ${item.className}`}
          />
        ))}
      </div>
    </section>
  )
}

export const Footer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const SocialIcon: React.FC<SocialIconProps> = ({ Icon, href, label }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#1F80FF] transition-colors">
      <Icon className="w-5 h-5" />
      <span className="sr-only">{label}</span>
    </a>
  )

  return (
    <footer className="py-16 relative z-20">
      <div className="container">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-12">
          <div className="space-y-3">
            <a href={localizedPath('/', lang)} className="flex items-center space-x-2">
              <img src="/currents-logo.png" alt="Currents Logo" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-black tracking-tight">Currents</span>
            </a>
            <p className="text-sm text-muted-foreground max-w-[220px]">
              {t('footer.tagline')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-16">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t('footer.columns.product')}</h3>
              <ul className="space-y-2.5">
                <li><a href={localizedPath('/pro', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.pro')}</a></li>
                <li><a href={localizedPath('/compatibility', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.compatibility')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t('footer.columns.company')}</h3>
              <ul className="space-y-2.5">
                <li><a href={localizedPath('/about', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.about')}</a></li>
                <li><a href={localizedPath('/research', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.research')}</a></li>
                <li><a href={localizedPath('/blog', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.blog')}</a></li>
                <li><a href="/destinations" className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.destinations')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t('footer.columns.legal')}</h3>
              <ul className="space-y-2.5">
                <li><a href={localizedPath('/terms-of-service', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.terms')}</a></li>
                <li><a href={localizedPath('/privacy-policy', lang)} className="text-sm text-foreground/80 hover:text-foreground">{t('footer.links.privacy')}</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-2 flex items-center justify-between gap-5">
          <div className="flex items-center space-x-5">
            <SocialIcon Icon={FaInstagram} href="https://www.instagram.com/currentsapp" label="Currents Instagram" />
            <SocialIcon Icon={FaTwitter} href="https://twitter.com/currentsapp" label="Currents Twitter" />
            <SocialIcon Icon={FaTiktok} href="https://www.tiktok.com/@currentsapp" label="Currents TikTok" />
          </div>
          <LanguageSwitcher className="text-xs" />
        </div>
      </div>
    </footer>
  )
}

const LandingPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/" />
      <Header />
      <main className="flex-1">
        <div className="px-3 pt-3">
          <div className="panel-hero rounded-[2.5rem]">
            <HeroSection />
            <AppShowcase />
          </div>
        </div>
        <PartnersSection />
        <FeaturesSection />
        <EssaySection />
        <ReviewsSection />
        <CtaSection />
      </main>
      <GradientBackground />
      <Footer />
      <Analytics />
    </div>
  )
}

export default LandingPage
